import { useEffect, useState } from "react";
import axios from "axios";
import { EyeOff, Maximize2, Minimize2 } from "lucide-react";
import { API } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { ResultsGroup } from "@/components/fly/ResultsGroup";
import { Podium } from "@/components/fly/Podium";
import { useT, tDay } from "@/lib/i18n";

export default function PublicResults() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [fs, setFs] = useState(false);

  useEffect(() => {
    const on = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  useEffect(() => {
    if (!fs) { return undefined; }
    let pause = 5, top = false, t;
    const step = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (pause > 0) { pause -= 1; return; }
      if (top) { top = false; pause = 5; window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      if (window.scrollY >= max - 2) { top = true; pause = 5; return; }
      window.scrollBy({ top: 40, behavior: "smooth" });
    };
    t = setInterval(step, 1000);
    return () => { clearInterval(t); window.scrollTo({ top: 0 }); };
  }, [fs]);
  const toggleFs = () => {
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    const active = document.fullscreenElement || document.webkitFullscreenElement;
    if (!req) { setFs((v) => !v); return; }
    Promise.resolve().then(() => (active ? exit.call(document) : req.call(el))).catch(() => setFs((v) => !v));
  };
  const fsBtn = (
    <button data-testid="public-fullscreen-btn" onClick={toggleFs} className="h-10 px-3 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-2 text-sm">
      {fs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}<span className="hidden sm:inline">{fs ? t("res.fsExit") : t("res.fs")}</span>
    </button>
  );

  useEffect(() => {
    const load = () => axios.get(`${API}/public/results`).then((r) => setData(r.data));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const current = data?.groups?.[0];

  return (
    <>
      {fs ? <div style={{ position: "fixed" }} className="top-4 right-4 z-40">{fsBtn}</div> : <TopBar lang title={t("res.title")} right={
        <>
          <span className="flex items-center gap-2 font-mono text-xs text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400 live-dot" />LIVE</span>
          {fsBtn}
        </>
      } />}
      <main data-testid="public-results-main" style={fs ? { zoom: Math.max(1.3, Math.min(window.innerWidth / 1100, window.innerHeight / 620)) } : undefined}
        className={fs ? "min-h-screen px-6 py-6 max-w-[1100px] mx-auto" : "max-w-5xl mx-auto px-4 sm:px-6 py-8"}>
        {!fs && <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">FLY CUP 2026</p>}
        <h1 hidden={fs} data-testid="public-results-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-red-500">{t("res.title")}</h1>
        {data && !data.published && (
          <div data-testid="public-results-hidden" className="fly-card p-10 mt-8 text-center text-slate-400"><EyeOff className="w-8 h-8 mx-auto mb-3" />{t("res.hidden")}</div>
        )}
        {data?.published && !current && (
          <div data-testid="public-results-empty" className="fly-card p-10 mt-8 text-center text-slate-400">{t("res.empty")}</div>
        )}
        {current && (
          <div key={current.key} className="mt-8" data-testid="public-current-category">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">{t("res.current")} · {tDay(current.day, lang)}</p>
            <Podium entries={current.entries} />
            <div className="mt-6"><ResultsGroup g={current} /></div>
          </div>
        )}
      </main>
    </>
  );
}
